import { useMemo } from 'react'
import {
  matrizDelMes,
  isoLocal,
  type MesRepartos,
  type RepartoMes,
} from '../../hooks/useCalendarioMes'
import { COLORES_DIA_LISTA } from '../agendamiento/coloresDia'
import { marcaDeEstado, MARCAS_LISTA } from '../agendamiento/estadoReparto'

/**
 * Calendario MENSUAL de repartos (referencia HEKKIO): rejilla lunes→domingo,
 * navegación mes anterior/hoy/siguiente, cada reparto como una píldora dentro de
 * su día, y leyenda abajo (rutas + estados).
 *
 * Componente PRESENTACIONAL: recibe los repartos por props y no sabe de la API,
 * igual que `CalendarioSemana`. La página que lo monta le pasa los datos del hook
 * `useCalendarioMes` y decide qué hacer al pulsar un día.
 *
 * DOBLE LECTURA DEL COLOR (regla del panel): la píldora combina dos datos sin que
 * ninguno dependa solo del color — FONDO = ruta (coloresDia), MARCA = estado
 * (símbolo + borde, estadoReparto). El nombre y el bono van siempre en texto.
 */

const CABECERAS = ['Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb', 'Dom'] as const

const MESES = [
  'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
  'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre',
] as const

interface Props {
  /** Repartos del mes, agrupados por fecha ISO (del hook useCalendarioMes). */
  repartosPorFecha: MesRepartos
  /** Año y mes (0-11) que se muestran. Los controla la página para el fetch. */
  anio: number
  mes: number
  /** Navegación: la página cambia anio/mes y refetch. */
  onCambiarMes: (anio: number, mes: number) => void
  /** Clic en un día: la página abre el panel del día (ver + añadir). */
  onSeleccionarDia: (isoFecha: string) => void
}

/** Una píldora de reparto: fondo por ruta, marca por estado, texto nombre·bono. */
function Pildora({ reparto }: { reparto: RepartoMes }) {
  // El color de fondo sale de la RUTA. Si no hay ruta conocida, tono neutro.
  const color = reparto.ruta
    ? COLORES_DIA_LISTA.find((c) => c.clave === reparto.ruta)
    : undefined
  const fondo = color ? `${color.fondo} ${color.tinta}` : 'bg-superficie text-tinta-2'
  const marca = marcaDeEstado(reparto.estado)

  // Etiqueta accesible: nombre, bono, ruta y estado en palabras (nunca símbolo solo).
  const aria = [
    reparto.cliente,
    reparto.bono ?? null,
    reparto.ruta ? reparto.ruta.toUpperCase() : null,
    marca.etiqueta,
  ]
    .filter(Boolean)
    .join(', ')

  return (
    <li
      aria-label={aria}
      title={aria}
      className={['flex items-center gap-1 rounded px-1.5 py-0.5 text-left', fondo, marca.borde].join(' ')}
    >
      <span aria-hidden className="shrink-0 text-[10px] leading-none">{marca.simbolo}</span>
      <span className="min-w-0 flex-1 truncate text-xs font-medium leading-tight">
        {reparto.cliente}
        {reparto.bono ? <span className="font-normal opacity-80"> · {reparto.bono}</span> : null}
        {reparto.ruta ? <span className="font-normal uppercase opacity-70"> · {reparto.ruta}</span> : null}
      </span>
    </li>
  )
}

/** Una celda del mes: número de día + sus repartos, pulsable para abrir el día. */
function CeldaDia({
  fecha,
  iso,
  delMes,
  repartos,
  esHoy,
  onSeleccionarDia,
}: {
  fecha: Date
  iso: string
  delMes: boolean
  repartos: RepartoMes[]
  esHoy: boolean
  onSeleccionarDia: (iso: string) => void
}) {
  const carga = repartos.length
  return (
    <button
      type="button"
      onClick={() => onSeleccionarDia(iso)}
      aria-label={`${fecha.getDate()}, ${carga} ${carga === 1 ? 'reparto' : 'repartos'}. Abrir el día`}
      className={[
        'flex min-h-[92px] flex-col gap-1 rounded-lg p-1.5 text-left ring-1 transition-colors',
        'hover:bg-superficie focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-foco',
        delMes ? 'bg-lienzo ring-borde-suave' : 'bg-superficie/40 ring-transparent',
        esHoy ? 'ring-2 ring-acento-borde' : '',
      ].join(' ')}
    >
      <span
        className={[
          'text-xs font-semibold tabular-nums',
          delMes ? 'text-tinta' : 'text-tinta-apagada',
          esHoy ? 'text-acento-tinta' : '',
        ].join(' ')}
      >
        {fecha.getDate()}
      </span>
      {carga > 0 ? (
        <ul className="flex flex-col gap-0.5">
          {repartos.slice(0, 3).map((r) => (
            <Pildora key={r.id} reparto={r} />
          ))}
          {carga > 3 ? (
            <li className="px-1 text-[11px] text-tinta-apagada">+{carga - 3} más</li>
          ) : null}
        </ul>
      ) : null}
    </button>
  )
}

export function CalendarioMes({
  repartosPorFecha,
  anio,
  mes,
  onCambiarMes,
  onSeleccionarDia,
}: Props) {
  const semanas = useMemo(() => matrizDelMes(anio, mes), [anio, mes])
  const hoyIso = isoLocal(new Date())

  const irAnterior = () => {
    const d = new Date(anio, mes - 1, 1)
    onCambiarMes(d.getFullYear(), d.getMonth())
  }
  const irSiguiente = () => {
    const d = new Date(anio, mes + 1, 1)
    onCambiarMes(d.getFullYear(), d.getMonth())
  }
  const irHoy = () => {
    const d = new Date()
    onCambiarMes(d.getFullYear(), d.getMonth())
  }

  return (
    <div className="flex flex-col gap-3">
      {/* Barra de navegación: Hoy + mes actual + flechas, como la referencia. */}
      <div className="flex items-center justify-between gap-2">
        <h2 className="text-sm font-semibold text-tinta">
          {MESES[mes]} de {anio}
        </h2>
        <div className="flex items-center gap-1">
          <button
            type="button"
            onClick={irHoy}
            className="rounded-md px-3 py-1.5 text-sm font-medium text-tinta-2 ring-1 ring-borde hover:bg-superficie focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-foco"
          >
            Hoy
          </button>
          <button
            type="button"
            onClick={irAnterior}
            aria-label="Mes anterior"
            className="rounded-md px-2.5 py-1.5 text-sm text-tinta-2 ring-1 ring-borde hover:bg-superficie focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-foco"
          >
            ‹
          </button>
          <button
            type="button"
            onClick={irSiguiente}
            aria-label="Mes siguiente"
            className="rounded-md px-2.5 py-1.5 text-sm text-tinta-2 ring-1 ring-borde hover:bg-superficie focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-foco"
          >
            ›
          </button>
        </div>
      </div>

      {/* Cabeceras de los siete días. */}
      <div className="grid grid-cols-7 gap-1">
        {CABECERAS.map((c) => (
          <div key={c} className="px-1 py-1 text-center text-xs font-medium uppercase text-tinta-apagada">
            {c}
          </div>
        ))}
      </div>

      {/* La rejilla del mes: 7 columnas, una fila por semana. */}
      <div className="grid grid-cols-7 gap-1">
        {semanas.flatMap((semana) =>
          semana.map((celda) => (
            <CeldaDia
              key={celda.iso}
              fecha={celda.fecha}
              iso={celda.iso}
              delMes={celda.delMes}
              esHoy={celda.iso === hoyIso}
              repartos={repartosPorFecha[celda.iso] ?? []}
              onSeleccionarDia={onSeleccionarDia}
            />
          )),
        )}
      </div>

      {/* Leyenda: rutas (color de fondo) + estados (marca). Dos ejes, dos filas. */}
      <div className="flex flex-col gap-2 border-t border-borde-suave pt-3">
        <div className="flex flex-wrap items-center gap-x-4 gap-y-1">
          <span className="text-xs font-medium text-tinta-apagada">Ruta:</span>
          {COLORES_DIA_LISTA.map((c) => (
            <span key={c.clave} className="flex items-center gap-1.5 text-xs text-tinta-2">
              <span className={['inline-block h-3 w-3 rounded-sm ring-1', c.fondo, c.anillo].join(' ')} aria-hidden />
              {c.etiqueta}
            </span>
          ))}
        </div>
        <div className="flex flex-wrap items-center gap-x-4 gap-y-1">
          <span className="text-xs font-medium text-tinta-apagada">Estado:</span>
          {MARCAS_LISTA.map((m) => (
            <span key={m.clave} className="flex items-center gap-1.5 text-xs text-tinta-2">
              <span aria-hidden className="text-[11px]">{m.simbolo}</span>
              {m.etiqueta}
            </span>
          ))}
        </div>
      </div>
    </div>
  )
}
